import type { User as SupabaseUser } from '@supabase/supabase-js';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { getUserIdFromSupabaseUser } from '@/lib/auth-utils';
import { broadcastAuthChange } from '@/lib/cross-tab-sync';
import { handleUserFacingError } from '@/lib/error-handler';
import { ensureRealtimeSubscriptions } from '@/lib/realtime/ensure-realtime';
import { clearSentryUser, setSentryUser } from '@/lib/sentry';
import { supabase } from '@/lib/supabase';
import { syncUserData } from '@/lib/sync/orchestrator';
import type { UserId } from '@/schemas/ids';
import { useRealtimeStore } from './realtime-store';

function isSessionMissingError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybeError = error as { message?: string; status?: number };
  const message = maybeError.message?.toLowerCase() ?? '';
  return maybeError.status === 403 || message.includes('session missing');
}

interface StorageLike {
  getItem?: (key: string) => string | null;
  removeItem?: (key: string) => void;
}

function getSupabaseAuthStorage(): {
  storageKey?: string;
  storage?: StorageLike;
} {
  const auth = supabase.auth as unknown as {
    storageKey?: string;
    storage?: StorageLike;
  };

  return {
    storageKey: auth.storageKey,
    storage: auth.storage,
  };
}

function collectSupabaseAuthStorages(storage?: StorageLike): StorageLike[] {
  const sources: StorageLike[] = [];

  if (storage && typeof storage.getItem === 'function') {
    sources.push(storage);
  }

  if (typeof globalThis !== 'undefined') {
    const globalStorage = Reflect.get(globalThis, 'localStorage') as StorageLike | undefined;
    if (globalStorage && typeof globalStorage.getItem === 'function') {
      sources.push(globalStorage);
    }
  }

  return sources;
}

function normalizeExpiresAtMs(value: number | undefined): number | undefined {
  if (value === undefined || Number.isNaN(value)) {
    return undefined;
  }

  const asMs = value > 10_000_000_000 ? value : value * 1000;
  return Number.isFinite(asMs) ? asMs : undefined;
}

function parseExpiresAt(raw: string): { expiresAtMs?: number; malformed: boolean } {
  try {
    const parsed = JSON.parse(raw) as {
      currentSession?: { expires_at?: number | null } | null;
      expiresAt?: number | null;
    };

    let expiresAtCandidate: number | undefined;
    if (typeof parsed?.expiresAt === 'number') {
      expiresAtCandidate = parsed.expiresAt;
    } else if (typeof parsed?.currentSession?.expires_at === 'number') {
      expiresAtCandidate = parsed.currentSession.expires_at;
    }

    const expiresAtMs = normalizeExpiresAtMs(expiresAtCandidate ?? undefined);
    return { expiresAtMs, malformed: false };
  } catch {
    return { malformed: true };
  }
}

function clearLocalSupabaseSession(): void {
  try {
    const { storageKey, storage } = getSupabaseAuthStorage();

    if (storageKey && storage && typeof storage.removeItem === 'function') {
      storage.removeItem(storageKey);
      return;
    }

    if (storageKey && typeof globalThis !== 'undefined') {
      const globalStorage = Reflect.get(globalThis, 'localStorage') as Storage | undefined;
      globalStorage?.removeItem(storageKey);
      return;
    }

    if (typeof globalThis !== 'undefined') {
      const globalStorage = Reflect.get(globalThis, 'localStorage') as Storage | undefined;
      if (!globalStorage) return;
      // Fallback: remove any Supabase auth tokens that match sb-*-auth-token pattern.
      for (const key of Object.keys(globalStorage)) {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          globalStorage.removeItem(key);
        }
      }
    }
  } catch (error) {
    console.warn('Failed to clear Supabase auth storage', error);
  }
}

function pruneExpiredSupabaseSession(): void {
  try {
    const { storageKey, storage } = getSupabaseAuthStorage();
    if (!storageKey) {
      return;
    }

    const sources = collectSupabaseAuthStorages(storage);

    for (const source of sources) {
      const raw = source.getItem?.(storageKey);
      if (!raw) {
        continue;
      }

      const { expiresAtMs, malformed } = parseExpiresAt(raw);

      if (malformed) {
        source.removeItem?.(storageKey);
        continue;
      }

      if (expiresAtMs === undefined) {
        continue;
      }

      if (expiresAtMs <= Date.now()) {
        source.removeItem?.(storageKey);
      }
    }
  } catch (error) {
    console.warn('Failed to prune expired Supabase session', error);
  }
}

interface AuthState {
  user: SupabaseUser | undefined;
  userId: UserId | undefined;
  isLoading: boolean;
  error: string | undefined;
  otpSent: boolean;
  email: string | undefined;
}

interface AuthActions {
  signInWithEmail: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  setUser: (user: SupabaseUser | undefined) => void;
  setError: (error: string | undefined) => void;
  setLoading: (isLoading: boolean) => void;
  setOtpSent: (sent: boolean) => void;
  setEmail: (email: string | undefined) => void;
  resetOtp: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      user: undefined,
      userId: undefined,
      isLoading: true, // Start as loading while we check for existing session
      error: undefined,
      otpSent: false,
      email: undefined,

      // Sign in with email OTP
      signInWithEmail: async (email: string) => {
        set((draft) => {
          draft.isLoading = true;
          draft.error = undefined;
        });

        try {
          const { error: otpError } = await supabase.auth.signInWithOtp({
            email,
            options: {
              emailRedirectTo: `${globalThis.location.origin}/auth/callback`,
            },
          });

          if (otpError) {
            const errorMessage = handleUserFacingError(
              otpError,
              'Failed to send verification code. Please try again.',
              { operation: 'signInWithEmail', email },
            );
            set((draft) => {
              draft.error = errorMessage;
              draft.isLoading = false;
            });
            throw otpError;
          }

          set((draft) => {
            draft.otpSent = true;
            draft.email = email;
            draft.isLoading = false;
          });
        } catch (error) {
          const errorMessage = handleUserFacingError(
            error,
            'Failed to send verification code. Please try again.',
            { operation: 'signInWithEmail', email },
          );
          set((draft) => {
            draft.error = errorMessage;
            draft.isLoading = false;
          });
        }
      },

      // Verify OTP
      verifyOtp: async (email: string, token: string) => {
        set((draft) => {
          draft.isLoading = true;
          draft.error = undefined;
        });

        try {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            email,
            token,
            type: 'email',
          });

          if (verifyError) {
            const errorMessage = handleUserFacingError(
              verifyError,
              'Invalid verification code. Please try again.',
              { operation: 'verifyOtp', email },
            );
            set((draft) => {
              draft.error = errorMessage;
              draft.isLoading = false;
            });
            throw verifyError;
          }

          // The auth state change listener will update the store
          set((draft) => {
            draft.otpSent = false;
            draft.email = undefined;
            draft.isLoading = false;
          });
        } catch (error) {
          const errorMessage = handleUserFacingError(
            error,
            'Invalid verification code. Please try again.',
            { operation: 'verifyOtp', email },
          );
          set((draft) => {
            draft.error = errorMessage;
            draft.isLoading = false;
          });
          throw error;
        }
      },

      // Sign out
      signOut: async () => {
        const finalizeSignOut = () => {
          useRealtimeStore.getState().disableRealtime();
          set((draft) => {
            draft.user = undefined;
            draft.userId = undefined;
            draft.isLoading = false;
            draft.error = undefined;
            draft.otpSent = false;
            draft.email = undefined;
          });
        };

        set((draft) => {
          draft.isLoading = true;
          draft.error = undefined;
        });

        try {
          const { error: signOutError } = await supabase.auth.signOut();

          if (signOutError) {
            if (isSessionMissingError(signOutError)) {
              console.info('Sign out requested without active session. Clearing local state.');
              clearLocalSupabaseSession();
              finalizeSignOut();
              return;
            }

            throw signOutError;
          }

          clearLocalSupabaseSession();
          finalizeSignOut();
        } catch (error) {
          if (isSessionMissingError(error)) {
            console.info('Sign out encountered missing session. Clearing local state.');
            clearLocalSupabaseSession();
            finalizeSignOut();
            return;
          }

          console.error('Error signing out:', error);
          set((draft) => {
            draft.error = 'Failed to sign out. Please try again.';
            draft.isLoading = false;
          });
        }
      },

      // Get current user from Supabase session
      getCurrentUser: async () => {
        set((draft) => {
          draft.isLoading = true;
        });

        pruneExpiredSupabaseSession();

        try {
          // Use getSession() instead of getUser() - it doesn't require an active session
          const {
            data: { session },
            error,
          } = await supabase.auth.getSession();

          if (error) {
            const message = (error as { message?: string }).message?.toLowerCase() ?? '';
            if (
              message.includes('invalid refresh token') ||
              message.includes('refresh token not found')
            ) {
              clearLocalSupabaseSession();
              set((draft) => {
                draft.user = undefined;
                draft.userId = undefined;
                draft.isLoading = false;
                draft.error = undefined;
              });
              return;
            }

            const errorMessage = handleUserFacingError(error, 'Failed to get session', {
              operation: 'getSession',
            });
            set((draft) => {
              draft.error = errorMessage;
              draft.isLoading = false;
            });
            return;
          }

          if (session?.user) {
            set((draft) => {
              draft.user = session.user;
              draft.userId = getUserIdFromSupabaseUser(session.user);
              draft.isLoading = false;
              draft.error = undefined;
            });
          } else {
            // No session is not an error - user is just not logged in
            set((draft) => {
              draft.user = undefined;
              draft.userId = undefined;
              draft.isLoading = false;
              draft.error = undefined;
            });
          }
        } catch (error) {
          console.error('Error getting current user:', error);
          set((draft) => {
            draft.error = 'Failed to get current user';
            draft.isLoading = false;
          });
        }
      },

      // Set user (called by auth state change listener)
      setUser: (user: SupabaseUser | undefined) => {
        try {
          const previousUserId = get().userId;
          const newUserId = user ? getUserIdFromSupabaseUser(user) : undefined;

          set((draft) => {
            draft.user = user;
            draft.userId = newUserId;
            draft.isLoading = false;
            draft.error = undefined;
          });

          if (previousUserId !== newUserId) {
            broadcastAuthChange(newUserId);
          }

          // Update Sentry user context
          if (newUserId && user) {
            setSentryUser(newUserId, user.email, user.email);
          } else {
            clearSentryUser();
          }

          // Handle login: sync data and enable realtime
          if (!previousUserId && newUserId) {
            // User just logged in
            void (async () => {
              try {
                // Sync all user data
                await syncUserData(newUserId);
                // Enable realtime subscriptions
                await ensureRealtimeSubscriptions(newUserId);
              } catch (error) {
                const syncError = error instanceof Error ? error : new Error(String(error));
                console.error('Failed to sync user data on login:', syncError);
                // Don't block login on sync failure
              }
            })();
          }

          // Handle logout: disable realtime
          if (previousUserId && !newUserId) {
            // User just logged out
            useRealtimeStore.getState().disableRealtime();
          }
        } catch (error) {
          console.error('Error setting user:', error);
          set((draft) => {
            draft.error = 'Authentication error. Please try again.';
            draft.isLoading = false;
          });
        }
      },

      // Set error
      setError: (error: string | undefined) => {
        set((draft) => {
          draft.error = error;
        });
      },

      // Set loading state
      setLoading: (isLoading: boolean) => {
        set((draft) => {
          draft.isLoading = isLoading;
        });
      },

      // Set OTP sent state
      setOtpSent: (sent: boolean) => {
        set((draft) => {
          draft.otpSent = sent;
        });
      },

      // Set email
      setEmail: (email: string | undefined) => {
        set((draft) => {
          draft.email = email;
        });
      },

      // Reset OTP state
      resetOtp: () => {
        set((draft) => {
          draft.otpSent = false;
          draft.email = undefined;
          draft.error = undefined;
        });
      },
    })),
    { name: 'AuthStore' },
  ),
);
